import { formatStaffName } from "@/lib/format-data";
import { ALL_COLLECTION_METHODS_FOR_FILTERS } from "@/lib/lab-order-utils";
import type { LabStatusCategory } from "@/lib/lab-status";

import type { LabInfo, LabOrderFetchParams, LabVendor } from "@/types/lab";
import type { FirebaseProviderSummary } from "@/types/provider";

import {
  defineFilters,
  entitySetFilter,
  enumSetFilter,
  locationSetFilter,
  textFilter,
  tupleDateRangeFilter,
} from "@/features/table-filters/contract";

const STATUS_OPTIONS: { value: LabStatusCategory; label: string }[] = [
  { value: "draft", label: "Draft" },
  { value: "awaiting_purchase", label: "Awaiting purchase" },
  { value: "in_progress", label: "In progress" },
  { value: "results_ready", label: "Results ready" },
  { value: "completed", label: "Completed" },
  { value: "closed", label: "Closed" },
];

const VENDOR_OPTIONS: { value: LabVendor; label: string }[] = [
  { value: "junction", label: "Junction" },
  { value: "fullscript", label: "Fullscript" },
  { value: "access", label: "Access" },
  { value: "uploaded", label: "Uploaded" },
  { value: "in_house", label: "In-house" },
];

// The full enum, not the ordering picker's offered list. A Test Kit order can
// still be sitting in this list, so the filter has to be able to reach it even
// though staff can no longer place a new one.
const COLLECTION_METHOD_OPTIONS = ALL_COLLECTION_METHODS_FOR_FILTERS;

/**
 * The `provider` control is single-select — `SetFilterArgs.control` only allows
 * `"multiSelect" | "location"`, and the underlying `providerStrategy` implements
 * `toggle` as a replace, not an append. `physician_ids`/`creator_ids` are lists on
 * the backend, so these use a plain `multiSelect` with provider options supplied
 * by the caller, the same way `createPurchaseOrderFilterDefinitions({ suppliers })`
 * and `createStockFilterDefinitions({ locations })` handle external option data.
 */
export function createLabOrderFilterDefinitions({
  labs,
  providers,
}: {
  labs: LabInfo[];
  providers: FirebaseProviderSummary[];
}) {
  const providerOptions = providers.map((provider) => ({
    value: provider.id,
    label: formatStaffName(provider),
  }));
  const labOptions = labs.map((lab) => ({ value: lab.slug, label: lab.name }));

  return defineFilters<LabOrderFetchParams>()([
    textFilter<LabOrderFetchParams>({
      key: "patientName",
      label: "Patient name",
      operator: "contains",
      queryKey: "patient_name",
    }),
    textFilter<LabOrderFetchParams>({
      key: "patientId",
      label: "Patient",
      presented: false,
      queryKey: "patient_id",
    }),
    entitySetFilter<LabOrderFetchParams>({
      key: "orderIds",
      label: "Order",
      presented: false,
      queryKey: "order_ids",
    }),
    enumSetFilter<LabOrderFetchParams>({
      key: "status",
      label: "Status",
      options: STATUS_OPTIONS,
    }),
    enumSetFilter<LabOrderFetchParams>({
      key: "vendor",
      label: "Vendor",
      options: VENDOR_OPTIONS,
    }),
    entitySetFilter<LabOrderFetchParams>({
      key: "physicianIds",
      label: "Ordering provider",
      control: "multiSelect",
      options: providerOptions,
      queryKey: "physician_ids",
    }),
    entitySetFilter<LabOrderFetchParams>({
      key: "creatorIds",
      label: "Created by",
      control: "multiSelect",
      options: providerOptions,
      queryKey: "creator_ids",
    }),
    // Only orders placed through the in-app Junction wizard carry a session and
    // therefore a location. defaultPolicy "none" (not LOCATION_SCOPE_DEFAULT)
    // keeps every Fullscript and externally-synced order visible on load —
    // auto-applying the user's location would silently hide the rows this
    // clinic-wide page exists to surface. Mirrors createStockFilterDefinitions.
    locationSetFilter<LabOrderFetchParams>({
      key: "locationIds",
      includeUnassignedKey: "includeUnassigned",
      label: "Location",
      defaultPolicy: "none",
    }),
    tupleDateRangeFilter<LabOrderFetchParams>({
      key: "createdDate",
      label: "Created date",
      queryKey: "created_date",
    }),
    entitySetFilter<LabOrderFetchParams>({
      key: "labSlug",
      label: "Lab",
      options: labOptions,
    }),
    enumSetFilter<LabOrderFetchParams>({
      key: "collectionMethod",
      label: "Collection method",
      options: COLLECTION_METHOD_OPTIONS,
      queryKey: "collection_method",
    }),
    textFilter<LabOrderFetchParams>({
      key: "testName",
      label: "Test name",
      operator: "contains",
      queryKey: "test_name",
    }),
  ]);
}
